import { getLeagueDisplayName } from "@/hooks/useLeagueNames";

// League display names matching Polymarket's sidebar.
// Used to (a) render the league badge, and (b) decide whether to route an
// event to /sports/<slug> or /esports/<slug> via the `sport` field.
export const LEAGUE_CONFIG: Record<string, { name: string; icon?: string; sport: string }> = {
  // US Sports
  nba: { name: "NBA", sport: "basketball" },
  basketball: { name: "Basketball", sport: "basketball" },
  "ncaa-basketball": { name: "NCAA Basketball", sport: "basketball" },
  nfl: { name: "NFL", sport: "football" },
  nhl: { name: "NHL", sport: "hockey" },
  mlb: { name: "MLB", sport: "baseball" },
  cbb: { name: "NCAAB", sport: "basketball" },
  "ncaa-cbb": { name: "NCAAB", sport: "basketball" },
  ncaab: { name: "NCAAB", sport: "basketball" },
  cfb: { name: "CFB", sport: "football" },
  "ncaa-cfb": { name: "CFB", sport: "football" },
  wnba: { name: "WNBA", sport: "basketball" },
  // cwbb/ncaa-cwbb removed — does not exist as a Polymarket category
  soccer: { name: "Soccer", sport: "soccer" },
  mls: { name: "MLS", sport: "soccer" },
  kbo: { name: "KBO", sport: "baseball" },
  // Soccer - European
  epl: { name: "EPL", sport: "soccer" },
  lal: { name: "La Liga", sport: "soccer" },
  laliga: { name: "La Liga", sport: "soccer" },
  "la-liga": { name: "La Liga", sport: "soccer" },
  "la-liga-2": { name: "La Liga 2", sport: "soccer" },
  bun: { name: "Bundesliga", sport: "soccer" },
  bundesliga: { name: "Bundesliga", sport: "soccer" },
  sea: { name: "Serie A", sport: "soccer" },
  "serie-a": { name: "Serie A", sport: "soccer" },
  seriea: { name: "Serie A", sport: "soccer" },
  fl1: { name: "Ligue 1", sport: "soccer" },
  "ligue-1": { name: "Ligue 1", sport: "soccer" },
  "ligue-2": { name: "Ligue 2", sport: "soccer" },
  ligue1: { name: "Ligue 1", sport: "soccer" },
  ucl: { name: "UCL", sport: "soccer" },
  uel: { name: "UEL", sport: "soccer" },
  ucol: { name: "Conference League", sport: "soccer" },
  "efl-championship": { name: "Championship", sport: "soccer" },
  elc: { name: "Championship", sport: "soccer" },
  ere: { name: "Eredivisie", sport: "soccer" },
  eredivisie: { name: "Eredivisie", sport: "soccer" },
  por: { name: "Primeira Liga", sport: "soccer" },
  "primeira-liga": { name: "Primeira Liga", sport: "soccer" },
  primeiraliga: { name: "Primeira Liga", sport: "soccer" },
  tur: { name: "Super Lig", sport: "soccer" },
  spl: { name: "Saudi Professional League", sport: "soccer" },
  den: { name: "Denmark Superliga", sport: "soccer" },
  mex: { name: "Liga MX", sport: "soccer" },
  bra: { name: "Brazil Serie A", sport: "soccer" },
  bra2: { name: "Brazil Serie B", sport: "soccer" },
  arg: { name: "Argentina Primera", sport: "soccer" },
  "primera-divisin-argentina": { name: "Argentina Primera", sport: "soccer" },
  col1: { name: "Colombia Primera A", sport: "soccer" },
  chi1: { name: "Chile Primera", sport: "soccer" },
  per1: { name: "Peru Liga 1", sport: "soccer" },
  lib: { name: "Copa Libertadores", sport: "soccer" },
  sud: { name: "Copa Sudamericana", sport: "soccer" },
  aus: { name: "A-League", sport: "soccer" },
  kor: { name: "K-League", sport: "soccer" },
  csl: { name: "Chinese Super League", sport: "soccer" },
  "fa-cup": { name: "FA Cup", sport: "soccer" },
  "efl-cup": { name: "EFL Cup", sport: "soccer" },
  dfb: { name: "DFB-Pokal", sport: "soccer" },
  cdr: { name: "Copa del Rey", sport: "soccer" },
  cde: { name: "Copa de Espana", sport: "soccer" },
  itc: { name: "Coppa Italia", sport: "soccer" },
  "itc-2025": { name: "Coppa Italia", sport: "soccer" },
  "itc-2026": { name: "Coppa Italia", sport: "soccer" },
  itsb: { name: "Serie B", sport: "soccer" },
  ssc: { name: "Scottish Premiership", sport: "soccer" },
  "scottish-premiership": { name: "Scottish Premiership", sport: "soccer" },
  "premier-league-2025": { name: "EPL", sport: "soccer" },
  "premier-league-2026": { name: "EPL", sport: "soccer" },
  "serie-b": { name: "Serie B", sport: "soccer" },
  "dfb-pokal": { name: "DFB-Pokal", sport: "soccer" },
  mar1: { name: "Morocco Botola Pro", sport: "soccer" },
  egy1: { name: "Egypt Premier League", sport: "soccer" },
  cze1: { name: "Czechia Fortuna Liga", sport: "soccer" },
  rou1: { name: "Romania SuperLiga", sport: "soccer" },
  rus: { name: "Russian Premier League", sport: "soccer" },
  nor: { name: "Eliteserien", sport: "soccer" },
  chi: { name: "Chinese Super League", sport: "soccer" },
  efa: { name: "EFL Trophy", sport: "soccer" },
  "soccer-el2": { name: "Europa League 2", sport: "soccer" },
  "fifa-friendlies": { name: "FIFA Friendlies", sport: "soccer" },
  "japan-j-league": { name: "J-League", sport: "soccer" },
  "japan-j2-league": { name: "J2 League", sport: "soccer" },
  ja2: { name: "J-League", sport: "soccer" },
  // Cricket
  cricipl: { name: "IPL", sport: "cricket" },
  criclcl: { name: "Lanka Premier", sport: "cricket" },
  cricpsl: { name: "PSL", sport: "cricket" },
  crint: { name: "Cricket Intl", sport: "cricket" },
  // Basketball - International
  bkbsl: { name: "BSL", sport: "basketball" },
  cwbb: { name: "College WBB", sport: "basketball" },
  euroleague: { name: "Euroleague Basketball", sport: "basketball" },
  bkcl: { name: "Champions League", sport: "basketball" },
  "basketball-champions-league": { name: "Champions League", sport: "basketball" },
  bkligend: { name: "Liga Endesa", sport: "basketball" },
  bkseriea: { name: "Serie A", sport: "basketball" },
  bknbl: { name: "NBL", sport: "basketball" },
  bkcba: { name: "CBA", sport: "basketball" },
  bkfr1: { name: "Pro A", sport: "basketball" },
  bkarg: { name: "LNB", sport: "basketball" },
  bkkbl: { name: "KBL", sport: "basketball" },
  // Hockey
  ahl: { name: "American Hockey League", sport: "hockey" },
  khl: { name: "KHL", sport: "hockey" },
  "khl-2025": { name: "KHL", sport: "hockey" },
  "khl-2026": { name: "KHL", sport: "hockey" },
  shl: { name: "SHL", sport: "hockey" },
  "shl-2025": { name: "SHL", sport: "hockey" },
  "shl-2026": { name: "SHL", sport: "hockey" },
  dehl: { name: "Deutsche Eishockey Liga", sport: "hockey" },
  cehl: { name: "Czech Extraliga", sport: "hockey" },
  snhl: { name: "Swiss National League", sport: "hockey" },
  // Motorsports
  formula1: { name: "Formula 1", sport: "motorsports" },
  // Tennis
  atp: { name: "ATP", sport: "tennis" },
  wta: { name: "WTA", sport: "tennis" },
  // Esports (long slugs from Polymarket + short slugs from Gamma API).
  // We keep the *long* game name on the badge (e.g. "Counter-Strike",
  // "League of Legends") because Polymarket prefixes esports titles with
  // the same long name ("Counter-Strike: Team A vs Team B"). The
  // tournament parser below would otherwise pull "Counter-Strike" out
  // and render a second, near-identical chip — so we both expand the
  // league name and suppress the parsed-tournament chip when it dupes.
  "counter-strike": { name: "Counter-Strike", sport: "esports" },
  "counter-strike-2": { name: "Counter-Strike", sport: "esports" },
  cs2: { name: "Counter-Strike", sport: "esports" },
  csgo: { name: "Counter-Strike", sport: "esports" }, // post-`TICKER_TO_TEAMS_LEAGUE` mapping (cs2→csgo)
  "league-of-legends": { name: "League of Legends", sport: "esports" },
  lol: { name: "League of Legends", sport: "esports" },
  "dota-2": { name: "Dota 2", sport: "esports" },
  dota2: { name: "Dota 2", sport: "esports" },
  dt2: { name: "Dota 2", sport: "esports" },
  valorant: { name: "Valorant", sport: "esports" },
  val: { name: "Valorant", sport: "esports" },
  "rainbow-six-siege": { name: "Rainbow Six Siege", sport: "esports" },
  r6siege: { name: "Rainbow Six Siege", sport: "esports" },
  r6: { name: "Rainbow Six Siege", sport: "esports" },
  "mobile-legends-bang-bang": { name: "Mobile Legends", sport: "esports" },
  mlbb: { name: "Mobile Legends", sport: "esports" },
  overwatch: { name: "Overwatch", sport: "esports" },
  ow: { name: "Overwatch", sport: "esports" },
  ow2: { name: "Overwatch 2", sport: "esports" },
  "honor-of-kings": { name: "Honor of Kings", sport: "esports" },
  hok: { name: "Honor of Kings", sport: "esports" },
  kog: { name: "Honor of Kings", sport: "esports" }, // post-`TICKER_TO_TEAMS_LEAGUE` mapping (hok→kog)
  "call-of-duty": { name: "Call of Duty", sport: "esports" },
  codmw: { name: "Call of Duty", sport: "esports" },
  "rocket-league": { name: "Rocket League", sport: "esports" },
  rl: { name: "Rocket League", sport: "esports" },
  "starcraft-2": { name: "StarCraft II", sport: "esports" },
  sc2: { name: "StarCraft 2", sport: "esports" },
  "starcraft-brood-war": { name: "StarCraft: Brood War", sport: "esports" },
  sc: { name: "StarCraft", sport: "esports" },
  wildrift: { name: "Wild Rift", sport: "esports" },
  pubg: { name: "PUBG", sport: "esports" },
  fifa: { name: "EA FC", sport: "esports" },
  // Combat
  ufc: { name: "UFC", sport: "mma" },
  zuffa: { name: "Zuffa", sport: "mma" },
  boxing: { name: "Boxing", sport: "boxing" },
  // Cricket
  ipl: { name: "IPL", sport: "cricket" },
  "international-cricket": { name: "International", sport: "cricket" },
  craus: { name: "Australia", sport: "cricket" },
  crban: { name: "Bangladesh", sport: "cricket" },
  creng: { name: "England", sport: "cricket" },
  crind: { name: "India", sport: "cricket" },
  crnew: { name: "New Zealand", sport: "cricket" },
  crpak: { name: "Pakistan", sport: "cricket" },
  crsou: { name: "South Africa", sport: "cricket" },
  cruae: { name: "UAE", sport: "cricket" },
  cru19wc: { name: "U19 World Cup", sport: "cricket" },
  crwpl: { name: "WPL", sport: "cricket" },
  crwncl: { name: "WNCL", sport: "cricket" },
  crwwc: { name: "Women's World Cup", sport: "cricket" },
  // Rugby
  rugby: { name: "Rugby", sport: "rugby" },
  "super-rugby": { name: "Super Rugby Pacific", sport: "rugby" },
  rusrp: { name: "Super Rugby Pacific", sport: "rugby" },
  ruprem: { name: "Premiership Rugby", sport: "rugby" },
  rutopft: { name: "Top 14", sport: "rugby" },
  rusixnat: { name: "Six Nations", sport: "rugby" },
  rueuchamp: { name: "European Rugby Champions Cup", sport: "rugby" },
  ruurc: { name: "United Rugby Championship", sport: "rugby" },
  ruchamp: { name: "The Rugby Championship", sport: "rugby" },
  // Winter Olympics
  mwoh: { name: "Men's Ice Hockey", sport: "winter-olympics" },
  "mens-winter-olympics-hockey": { name: "Men's Ice Hockey", sport: "winter-olympics" },
  wwoh: { name: "Women's Ice Hockey", sport: "winter-olympics" },
  "womens-winter-olympics-hockey": { name: "Women's Ice Hockey", sport: "winter-olympics" },
  "winter-olympics-all": { name: "All Events", sport: "winter-olympics" },
  "winter-olympics": { name: "Winter Olympics", sport: "winter-olympics" },
  // Other
  golf: { name: "Golf", sport: "golf" },
  f1: { name: "Formula 1", sport: "motorsports" },
  chess: { name: "Chess", sport: "chess" },
  pickleball: { name: "Pickleball", sport: "pickleball" },
};

// Get league display name with readable fallback
// Priority: global cache (from API) > dynamicLeagues (local state) > LEAGUE_CONFIG (static) > formatted slug
export function getLeagueName(slug: string, dynamicLeagues?: Record<string, { name: string; sport: string }>): string {
  const global = getLeagueDisplayName(slug);
  // getLeagueDisplayName returns formatted slug as fallback — check if it's a real name
  if (global !== slug.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')) return global;
  return dynamicLeagues?.[slug]?.name || LEAGUE_CONFIG[slug]?.name || global;
}

// Detect if an event has a livestream based on resolutionSource URL
export function getStreamData(resolutionSource?: string | null): { type: 'twitch' | 'kick' | 'youtube'; channel: string; embedUrl: string } | null {
  if (!resolutionSource) return null;
  try {
    if (resolutionSource.includes('twitch.tv')) {
      const channel = new URL(resolutionSource).pathname.split('/').filter(Boolean).pop();
      if (!channel) return null;
      return { type: 'twitch', channel, embedUrl: `https://player.twitch.tv/?channel=${channel}&parent=${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}` };
    }
    if (resolutionSource.includes('kick.com')) {
      const channel = new URL(resolutionSource).pathname.split('/').filter(Boolean).pop();
      if (!channel) return null;
      return { type: 'kick', channel, embedUrl: `https://player.kick.com/${channel}` };
    }
    if (resolutionSource.includes('youtube.com') || resolutionSource.includes('youtu.be')) {
      const url = new URL(resolutionSource);
      const videoId = url.searchParams.get('v') || url.pathname.split('/').pop();
      if (!videoId) return null;
      return { type: 'youtube', channel: videoId, embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1` };
    }
  } catch { /* invalid URL */ }
  return null;
}
